// src/Components/TopBar/ProfileButton.tsx
import React from 'react';
import {
  OrdersIcon,
  PaymentsIcon,
  ProfileClientIcon,
  SignoutIcon,
  DeleteIcon,
} from 'assets/icons';
import useGoToUserProfile from 'app/hooks/useGoToUserProfile';
import OverlayWindow from 'Components/OverlayWindow';
import { useCreatePopup } from 'Components/Popups';
import UserAvatar from 'Components/UserAvatar';
import UserRole from 'Components/UserRole';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'utils/AuthProvider';
import { errorToast, successToast } from 'Components/Notifications';
import { prepareHeaders } from 'utils';

export default function ProfileButton() {
  const { isClient, isAccountantAdmin, isAccountant, signOut, userId, user } =
    useAuth();

  const navigate = useNavigate();
  const ProfileDropDownMenu = useCreatePopup();
  const gotoUserProfile = useGoToUserProfile();

  // Function to handle profile deletion with double confirmation
  const deleteProfile = async () => {
    // First confirmation
    const firstConfirmed = window.confirm(
      'Are you sure you want to delete your profile? This action cannot be undone.',
    );
    if (!firstConfirmed) return;

    // Second confirmation to double make sure the user intends to delete their profile
    const secondConfirmed = window.confirm(
      'This is irreversible. Please confirm again that you want to permanently delete your profile.',
    );
    if (!secondConfirmed) return;

    try {
      // Use your app's header preparation and API URL
      const headers = await prepareHeaders(new Headers());
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/users/${userId}/destroy`,
        {
          method: 'DELETE',
          headers,
        },
      );

      if (response.ok) {
        successToast('Your profile has been deleted.');
        signOut(false);
      } else {
        const data = await response.json();
        errorToast(
          'Error: ' +
            (data.errors
              ? data.errors.join(', ')
              : 'Unable to delete your profile.'),
        );
      }
    } catch (error: any) {
      errorToast('Error: ' + error.message);
    }
  };

  return (
    <div className="tooltip-wrapper">
      <div
        ref={ProfileDropDownMenu.setAnchor}
        className="cursor-pointer text-gray-dark hover:text-opacity-90 relative w-8 h-8 shadow mr-2 rounded-full flex justify-center items-center button-wrapper"
        onClick={ProfileDropDownMenu.toggle}
      >
        <UserAvatar user={user} />
      </div>
      <span className="tooltip-text">Your profile</span>

      <ProfileDropDownMenu.Component>
        <OverlayWindow size="" paddingSize="p-4" border="">
          <div className="w-64 text-sm">
            <div>
              <div className="text-gray-dark font-bold capitalize">
                {user?.given_name} {user?.family_name}
              </div>
              <div className="text-gray-700 font-semibold">{user?.email}</div>
              <UserRole roles={user?.roles} />
            </div>

            {(isClient || isAccountant) && (
              <>
                <hr className="my-4 text-gray-light" />

                <div className="text-gray-dark font-bold space-y-4 my-4">
                  <div
                    className="cursor-pointer hover:underline flex items-center gap-x-4"
                    onClick={() => {
                      ProfileDropDownMenu.close();
                      user && gotoUserProfile(user);
                    }}
                  >
                    <ProfileClientIcon className="text-green" />
                    <span>Your Profile</span>
                  </div>
                  {isClient && (
                    <div
                      className="cursor-pointer hover:underline flex items-center gap-x-4"
                      onClick={() => {
                        ProfileDropDownMenu.close();
                        navigate('/dashboard/orders');
                      }}
                    >
                      <OrdersIcon className="text-green" />
                      <span>Orders</span>
                    </div>
                  )}
                  {(isClient || isAccountant) && (
                    <div
                      className="cursor-pointer hover:underline flex items-center gap-x-4"
                      onClick={() => {
                        ProfileDropDownMenu.close();
                        navigate('/dashboard/payments');
                      }}
                    >
                      <PaymentsIcon className="text-green" />
                      <span>Payments</span>
                    </div>
                  )}
                </div>
              </>
            )}

            <hr className="my-4 text-gray-400 bg-gray-400" />

            {/* Delete My Profile Button */}
            <div
              onClick={() => {
                ProfileDropDownMenu.close();
                deleteProfile();
              }}
              className="cursor-pointer hover:underline flex items-center gap-x-4"
            >
              <DeleteIcon className="text-red-dark" />
              <span className="text-gray-dark font-bold">
                Delete My Profile
              </span>
            </div>

            <hr className="my-4 text-gray-400 bg-gray-400" />

            <div
              onClick={() => signOut(false)}
              className="cursor-pointer hover:underline flex items-center gap-x-4"
            >
              <SignoutIcon className="text-red-dark" />
              <span className="text-gray-dark font-bold">Sign out</span>
            </div>
          </div>
        </OverlayWindow>
      </ProfileDropDownMenu.Component>
    </div>
  );
}
